import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { validateYesNoQuestion, validateMultipleChoiceQuestion, validateYesNoAnswer, validateMultipleChoiceAnswer } from './validation';
import type { YesNoValue, YesNoAnswer, MultipleChoiceAnswer, Answer, YesNoQuestion, MultipleChoiceQuestion, Question } from './types';

const yesNoOptions: (YesNoValue | "Skip question")[] = [
  'No',
  "I don't think so",
  "Skip question",
  'I think so',
  'Yes',
];

function Questions(): React.ReactElement {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [expandedQuestions, setExpandedQuestions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [showAnswered, setShowAnswered] = useState<boolean>(false);
  const [showUnanswered, setShowUnanswered] = useState<boolean>(true);
  const [showSkipped, setShowSkipped] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<Record<string, boolean>>({});
  const [displayedQuestions, setDisplayedQuestions] = useState<Question[]>([]);
  const [noteDebounceTimers, setNoteDebounceTimers] = useState<Record<string, NodeJS.Timeout>>({});

  // Importance options
  const importanceOptions: Array<{ value: 'preference' | 'important' | 'dealbreaker' | null; label: string }> = [
    { value: null, label: 'This is not important to me' },
    { value: 'preference', label: 'This is a preference to me' },
    { value: 'important', label: 'This is important to me' },
    { value: 'dealbreaker', label: 'This is a dealbreaker to me' },
  ];

  // Load questions and user's answers
  useEffect(() => {
    const loadQuestionsAndAnswers = async (): Promise<void> => {
      if (!user) return;

      try {
        // Fetch all questions from Firestore
        const questionsSnapshot = await getDocs(collection(db, 'questions'));
        const questionsData: Question[] = questionsSnapshot.docs.map(doc => {
          const data = doc.data();
          // Default to yes_no for backwards compatibility
          const type = data.type || 'yes_no';

          if (type === 'multiple_choice') {
            validateMultipleChoiceQuestion(doc.id, data);

            return {
              id: doc.id,
              text: data.text,
              type: 'multiple_choice',
              options: data.options || [],
              category: Array.isArray(data.category) ? data.category : (data.category ? [data.category] : []),
              class: data.class || 1,
            } as MultipleChoiceQuestion;
          } else {
            // Validate yes/no question
            validateYesNoQuestion(doc.id, data);

            return {
              id: doc.id,
              text: data.text,
              type: 'yes_no',
              category: Array.isArray(data.category) ? data.category : (data.category ? [data.category] : []),
              class: data.class || 1,
            } as YesNoQuestion;
          }
        });
        setQuestions(questionsData);

        // Fetch user's existing answers
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        const userAnswers: Record<string, Answer> = userDoc.exists() && userDoc.data().answers ? userDoc.data().answers : {};

        // Validate each answer
        for (const questionId in userAnswers) {
          const answer = userAnswers[questionId];
          const question = questionsData.find(q => q.id === questionId);

          if (question) {
            if (question.type === 'multiple_choice') {
              validateMultipleChoiceAnswer(questionId, answer);
            } else if (question.type === 'yes_no') {
              validateYesNoAnswer(questionId, answer);
            } else {
              console.error(`Unknown question type for question ${questionId}`);
            }
          }
        }

        setAnswers(userAnswers);

        // Set unanswered questions as expanded by default
        const expanded: Record<string, boolean> = {};
        questionsData.forEach(q => {
          const answer = userAnswers[q.id];

          if (q.type === 'yes_no') {
            expanded[q.id] = !answer || answer.myAnswer === null;
          } else if (q.type === 'multiple_choice') {
            // Multiple choice: expand if not answered or skipped
            const isAnswered = answer && (Array.isArray(answer.myAnswer) ? answer.myAnswer.length > 0 : answer.myAnswer === null);
            expanded[q.id] = !isAnswered;
          }
        });
        setExpandedQuestions(expanded);
      } catch (error) {
        console.error('Error loading questions:', error);
      }

      setLoading(false);
    };

    loadQuestionsAndAnswers();
  }, [user]);

  // Initialize selectedCategories with all categories checked
  React.useEffect(() => {
    const allCategories = Array.from(
      new Set(
        questions.flatMap(q =>
          q.category.length > 0 ? q.category : ['Uncategorized']
        )
      )
    );

    const newSelectedCategories: Record<string, boolean> = {};
    allCategories.forEach(cat => {
      if (selectedCategories[cat] === undefined) {
        newSelectedCategories[cat] = true;
      }
    });

    const hasNewCategories = Object.keys(newSelectedCategories).length > 0;
    if (hasNewCategories) {
      setSelectedCategories(prev => ({ ...prev, ...newSelectedCategories }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  // Helper function to check if a question is skipped
  const isQuestionSkipped = (q: Question): boolean => {
    const answer = answers[q.id];
    if (q.type === 'multiple_choice') {
      return answer && answer.myAnswer === null;
    } else if (q.type === 'yes_no') {
      return answer && answer.myAnswer === null;
    }
    return false;
  };

  // Helper function to check if a question is answered (not skipped, not unanswered)
  const isQuestionAnswered = (q: Question): boolean => {
    const answer = answers[q.id];
    if (q.type === 'yes_no') {
      return answer && answer.myAnswer !== null;
    } else if (q.type === 'multiple_choice') {
      return answer && Array.isArray(answer.myAnswer) && answer.myAnswer.length > 0;
    }
    return false;
  };

  // First filter: by answered/unanswered/skipped status
  const questionsAfterAnsweredFilter = questions.filter(q => {
    const answered = isQuestionAnswered(q);
    const skipped = isQuestionSkipped(q);
    const unanswered = !answered && !skipped;

    if (answered && showAnswered) {
      return true;
    } else if (skipped && showSkipped) {
      return true;
    } else if (unanswered && showUnanswered) {
      return true;
    } else {
      return false;
    }
  });

  // Get unique categories from first-filtered questions
  const availableCategories = Array.from(
    new Set(
      questionsAfterAnsweredFilter.flatMap(q =>
        q.category.length > 0 ? q.category : ['Uncategorized']
      )
    )
  ).sort();

  // Second filter: by category
  const filteredQuestions = questionsAfterAnsweredFilter.filter(q => {
    const categories = q.category.length > 0 ? q.category : ['Uncategorized'];
    // Show question if ANY of its categories are selected
    return categories.some(cat => selectedCategories[cat] !== false);
  });

  // Update displayed questions only when filters change
  React.useEffect(() => {
    // Sort: by class first (1=most important), then answered questions (by modifiedAt), then unanswered
    const sortedQuestions = [...filteredQuestions].sort((a, b) => {
      // First sort by class (lower number = more important = comes first)
      if (a.class !== b.class) {
        return a.class - b.class;
      }

      // Within same class, sort by answered status
      const aAnswered = isQuestionAnswered(a);
      const bAnswered = isQuestionAnswered(b);
      const aAnswer = answers[a.id];
      const bAnswer = answers[b.id];

      // Both answered: sort by modifiedAt (latest first)
      if (aAnswered && bAnswered) {
        const aTime = aAnswer?.modifiedAt || 0;
        const bTime = bAnswer?.modifiedAt || 0;
        return bTime - aTime;
      }

      // One answered, one not: answered first
      if (aAnswered && !bAnswered) return -1;
      if (!aAnswered && bAnswered) return 1;

      // Both unanswered: keep original order
      return 0;
    });

    setDisplayedQuestions(sortedQuestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnswered, showUnanswered, showSkipped, selectedCategories, questions, loading]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(noteDebounceTimers).forEach(timer => clearTimeout(timer));
    };
  }, [noteDebounceTimers]);

  // Toggle question expand/collapse
  const toggleQuestion = (questionId: string): void => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  // Handle answer change and auto-save (for yes/no questions)
  const handleAnswerChange = async (questionId: string, value: YesNoValue | null): Promise<void> => {
    const currentAnswer = answers[questionId] as YesNoAnswer | undefined;
    const mirrored = currentAnswer?.mirrored ?? true;

    const updatedAnswer: YesNoAnswer = {
      myAnswer: value,
      partnerPreference: !mirrored ? (currentAnswer?.partnerPreference ?? null) : null,
      mirrored: mirrored,
      importance: currentAnswer?.importance ?? 'preference',
      modifiedAt: Date.now(),
      note: currentAnswer?.note ?? null,
    };

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Auto-save the answer
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        answers: updatedAnswers,
      });
    } catch (error) {
      console.error('Error auto-saving answer:', error);
    }
  };

  // Handle multiple choice answer change
  const handleMultipleChoiceChange = async (questionId: string, option: string | null, isSkip: boolean = false): Promise<void> => {
    const currentAnswer = answers[questionId] || {};
    const mirrored = currentAnswer.mirrored !== undefined ? currentAnswer.mirrored : true;
    let updatedAnswer: MultipleChoiceAnswer;

    if (isSkip) {
      // Toggle skip
      updatedAnswer = {
        myAnswer: currentAnswer.myAnswer === null ? [] : null,
        partnerPreference: !mirrored && currentAnswer.partnerPreference !== undefined ? (Array.isArray(currentAnswer.partnerPreference) ? currentAnswer.partnerPreference : null) : null,
        mirrored: mirrored,
        importance: currentAnswer.importance !== undefined ? currentAnswer.importance : 'preference',
        modifiedAt: Date.now(),
        note: currentAnswer.note ?? null,
      };
    } else {
      // Toggle option in array
      const currentSelections = Array.isArray(currentAnswer.myAnswer) ? currentAnswer.myAnswer : [];
      const newSelections = currentSelections.includes(option as string)
        ? currentSelections.filter(opt => opt !== option)
        : [...currentSelections, option as string];

      updatedAnswer = {
        myAnswer: newSelections.length > 0 ? newSelections : [],
        partnerPreference: !mirrored && currentAnswer.partnerPreference !== undefined ? (Array.isArray(currentAnswer.partnerPreference) ? currentAnswer.partnerPreference : null) : null,
        mirrored: mirrored,
        importance: currentAnswer.importance !== undefined ? currentAnswer.importance : 'preference',
        modifiedAt: Date.now(),
        note: currentAnswer.note ?? null,
      };
    }

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Auto-save
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        answers: updatedAnswers,
      });
    } catch (error) {
      console.error('Error auto-saving multiple choice:', error);
    }
  };

  // Handle mirror toggle for yes/no questions
  const handleMirrorToggle = async (questionId: string, mirrored: boolean): Promise<void> => {
    const currentAnswer = answers[questionId] as YesNoAnswer | undefined;
    const updatedAnswer: YesNoAnswer = {
      myAnswer: currentAnswer?.myAnswer ?? null,
      partnerPreference: !mirrored ? (currentAnswer?.myAnswer ?? null) : null,
      mirrored: mirrored,
      importance: currentAnswer?.importance ?? 'preference',
      modifiedAt: Date.now(),
      note: currentAnswer?.note ?? null,
    };

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Auto-save
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        answers: updatedAnswers,
      });
    } catch (error) {
      console.error('Error toggling mirror:', error);
    }
  };

  // Handle mirror toggle for multiple choice questions
  const handleMultipleChoicePartnerPrefToggle = async (questionId: string, mirrored: boolean): Promise<void> => {
    const currentAnswer = answers[questionId] as MultipleChoiceAnswer | undefined;
    const updatedAnswer: MultipleChoiceAnswer = {
      myAnswer: Array.isArray(currentAnswer?.myAnswer) ? currentAnswer.myAnswer : currentAnswer?.myAnswer === null ? null : [],
      partnerPreference: !mirrored ? (Array.isArray(currentAnswer?.myAnswer) ? currentAnswer.myAnswer : []) : null,
      mirrored: mirrored,
      importance: currentAnswer?.importance ?? 'preference',
      modifiedAt: Date.now(),
      note: currentAnswer?.note ?? null,
    };

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Auto-save
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        answers: updatedAnswers,
      });
    } catch (error) {
      console.error('Error toggling partner preference:', error);
    }
  };

  // Handle multiple choice partner preference option change
  const handleMultipleChoicePartnerOptionChange = async (questionId: string, option: string): Promise<void> => {
    const currentAnswer = answers[questionId] as MultipleChoiceAnswer | undefined;
    const currentPreferences = Array.isArray(currentAnswer?.partnerPreference) ? currentAnswer.partnerPreference : [];
    const newPreferences = currentPreferences.includes(option)
      ? currentPreferences.filter(opt => opt !== option)
      : [...currentPreferences, option];

    const updatedAnswer: MultipleChoiceAnswer = {
      myAnswer: Array.isArray(currentAnswer?.myAnswer) ? currentAnswer.myAnswer : currentAnswer?.myAnswer === null ? null : [],
      partnerPreference: newPreferences,
      mirrored: currentAnswer?.mirrored ?? false,
      importance: currentAnswer?.importance ?? 'preference',
      modifiedAt: Date.now(),
      note: currentAnswer?.note ?? null,
    };

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Auto-save
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        answers: updatedAnswers,
      });
    } catch (error) {
      console.error('Error updating partner preference:', error);
    }
  };

  // Handle partner preference change (for yes/no questions)
  const handlePartnerPreferenceChange = async (questionId: string, value: YesNoValue | null): Promise<void> => {
    const currentAnswer = answers[questionId] as YesNoAnswer | undefined;
    const updatedAnswer: YesNoAnswer = {
      myAnswer: currentAnswer?.myAnswer ?? null,
      partnerPreference: value,
      mirrored: currentAnswer?.mirrored ?? false,
      importance: value !== null ? (currentAnswer?.importance ?? 'preference') : null,
      modifiedAt: Date.now(),
      note: currentAnswer?.note ?? null,
    };

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Auto-save
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        answers: updatedAnswers,
      });
    } catch (error) {
      console.error('Error auto-saving partner preference:', error);
    }
  };

  // Handle importance change (works for both yes/no and multiple choice, including null)
  const handleImportanceChange = async (questionId: string, importance: 'preference' | 'important' | 'dealbreaker' | null): Promise<void> => {
    const currentAnswer = answers[questionId];
    const question = questions.find(q => q.id === questionId);

    let updatedAnswer: Answer;
    if (question && question.type === 'multiple_choice') {
      const mcAnswer = currentAnswer as MultipleChoiceAnswer | undefined;
      const mirrored = mcAnswer?.mirrored ?? true;
      updatedAnswer = {
        myAnswer: Array.isArray(mcAnswer?.myAnswer) ? mcAnswer.myAnswer : mcAnswer?.myAnswer === null ? null : [],
        partnerPreference: !mirrored && mcAnswer?.partnerPreference ? mcAnswer.partnerPreference : null,
        mirrored: mirrored,
        importance: importance,
        modifiedAt: Date.now(),
        note: mcAnswer?.note ?? null,
      } as MultipleChoiceAnswer;
    } else if (question && question.type === 'yes_no') {
      const ynAnswer = currentAnswer as YesNoAnswer | undefined;
      const mirrored = ynAnswer?.mirrored ?? true;
      updatedAnswer = {
        myAnswer: ynAnswer?.myAnswer ?? null,
        partnerPreference: !mirrored && ynAnswer?.partnerPreference ? ynAnswer.partnerPreference : null,
        mirrored: mirrored,
        importance: importance,
        modifiedAt: Date.now(),
        note: ynAnswer?.note ?? null,
      } as YesNoAnswer;
    } else {
      console.error(`Unknown question type for question ${questionId}`);
      return;
    }

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Auto-save
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        answers: updatedAnswers,
      });
    } catch (error) {
      console.error('Error auto-saving importance:', error);
    }
  };

  // Handle note change for question answers
  const handleNoteChange = async (questionId: string, noteText: string): Promise<void> => {
    const currentAnswer = answers[questionId];
    const question = questions.find(q => q.id === questionId);

    if (!question) return;

    // Enforce 1000 character limit
    const truncatedNote = noteText.slice(0, 1000);

    // Update local state immediately
    let updatedAnswer: Answer;
    if (question.type === 'multiple_choice') {
      const mcAnswer = currentAnswer as MultipleChoiceAnswer | undefined;
      const mirrored = mcAnswer?.mirrored ?? true;
      updatedAnswer = {
        myAnswer: Array.isArray(mcAnswer?.myAnswer) ? mcAnswer.myAnswer : mcAnswer?.myAnswer === null ? null : [],
        partnerPreference: !mirrored && mcAnswer?.partnerPreference ? mcAnswer.partnerPreference : null,
        mirrored: mirrored,
        importance: mcAnswer?.importance ?? 'preference',
        modifiedAt: Date.now(),
        note: truncatedNote.trim() === '' ? null : truncatedNote,
      } as MultipleChoiceAnswer;
    } else if (question.type === 'yes_no') {
      const ynAnswer = currentAnswer as YesNoAnswer | undefined;
      const mirrored = ynAnswer?.mirrored ?? true;
      updatedAnswer = {
        myAnswer: ynAnswer?.myAnswer ?? null,
        partnerPreference: !mirrored && ynAnswer?.partnerPreference ? ynAnswer.partnerPreference : null,
        mirrored: mirrored,
        importance: ynAnswer?.importance ?? 'preference',
        modifiedAt: Date.now(),
        note: truncatedNote.trim() === '' ? null : truncatedNote,
      } as YesNoAnswer;
    } else {
      console.error(`Unknown question type for question ${questionId}`);
      return;
    }

    const updatedAnswers = {
      ...answers,
      [questionId]: updatedAnswer,
    };
    setAnswers(updatedAnswers);

    // Clear existing timer for this question
    if (noteDebounceTimers[questionId]) {
      clearTimeout(noteDebounceTimers[questionId]);
    }

    // Set new debounced save (1000ms)
    const timerId = setTimeout(async () => {
      if (!user) return;

      try {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, {
          answers: updatedAnswers,
        });
      } catch (error) {
        console.error('Error auto-saving note:', error);
      }

      // Clean up timer reference
      setNoteDebounceTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[questionId];
        return newTimers;
      });
    }, 1000);

    setNoteDebounceTimers(prev => ({
      ...prev,
      [questionId]: timerId,
    }));
  };

  // Get answer label from value
  const getYesNoLabel = (value: YesNoValue | null): string => {
    if (value === null) return '';
    return value;
  };

  // Get partner preference label
  const getPartnerPreferenceLabel = (value: YesNoValue | null): string => {
    if (value === null) return "Don't care";
    return value;
  };

  // Get importance label
  const getImportanceLabel = (importance: 'preference' | 'important' | 'dealbreaker' | null | undefined): string => {
    if (importance === 'preference') return 'Preference';
    if (importance === 'important') return 'Important';
    if (importance === 'dealbreaker') return 'Dealbreaker';
    return '';
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading questions...</div>;
  }

  if (questions.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>Questions</h2>
        <p>No questions available yet. Add questions to the 'questions' collection in Firestore.</p>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '20px'
          }}
        >
          Back to Profile
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/')}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '20px'
        }}
      >
        Back to Profile
      </button>

      <h1>Answer Questions</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Answer these questions to help us match you with compatible people.
      </p>

      {/* Show answered/unanswered/skipped questions checkboxes */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
        <div style={{ display: 'flex', gap: '20px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showUnanswered}
              onChange={(e) => setShowUnanswered(e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Show unanswered questions
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showAnswered}
              onChange={(e) => setShowAnswered(e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Show answered questions
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showSkipped}
              onChange={(e) => setShowSkipped(e.target.checked)}
              style={{ marginRight: '8px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Show skipped questions
            </span>
          </label>
        </div>

        {/* Category filters */}
        {availableCategories.length > 0 && (
          <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>
              Categories:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {availableCategories.map(category => (
                <label
                  key={category}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '5px 10px',
                    backgroundColor: selectedCategories[category] !== false ? '#e3f2fd' : '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories[category] !== false}
                    onChange={(e) => {
                      setSelectedCategories(prev => ({
                        ...prev,
                        [category]: e.target.checked
                      }));
                    }}
                    style={{ marginRight: '6px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px' }}>
                    {category || 'Uncategorized'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {displayedQuestions.map((question) => {
        const answer = answers[question.id];
        const isExpanded = expandedQuestions[question.id];

        // Render yes/no question
        if (question.type === 'yes_no') {
          // For yes/no questions
          const yesNoAnswer = answer as YesNoAnswer | undefined;
          const isAnswered = yesNoAnswer && yesNoAnswer.myAnswer !== null;
          const isSkipped = yesNoAnswer && yesNoAnswer.myAnswer === null;
          const partnerPrefEnabled = yesNoAnswer && yesNoAnswer.partnerPreference !== null;
          return (
            <div
              key={question.id}
              style={{
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: isAnswered ? '#e8f5e9' : isSkipped ? '#e3f2fd' : '#f5f5f5',
                borderRadius: '8px',
                border: isAnswered ? '1px solid #4CAF50' : isSkipped ? '1px solid #2196F3' : '1px solid #ddd',
              }}
            >
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                Class {question.class}
              </div>
              <div
                onClick={() => (isAnswered || isSkipped) && toggleQuestion(question.id)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: (isAnswered || isSkipped) ? 'pointer' : 'default',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '16px', flex: 1 }}>{question.text}</h3>
                {(isAnswered || isSkipped) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: isSkipped ? '#2196F3' : '#4CAF50', fontSize: '14px', fontWeight: 'bold' }}>
                        {isSkipped ? 'Skipped' : `My answer: ${getYesNoLabel(yesNoAnswer.myAnswer)}`}
                      </div>
                      {partnerPrefEnabled && (
                        <div style={{ color: '#2196F3', fontSize: '12px', marginTop: '2px' }}>
                          Want partner: {getPartnerPreferenceLabel(yesNoAnswer.partnerPreference)}
                          {yesNoAnswer.importance && (
                            <span style={{ marginLeft: '5px', fontStyle: 'italic' }}>
                              ({getImportanceLabel(yesNoAnswer.importance)})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: '20px', color: '#666' }}>
                      {isExpanded ? '▼' : '▶'}
                    </span>
                  </div>
                )}
              </div>

              {isExpanded && (
              <div style={{ marginTop: '15px' }}>
                {/* User's own answer */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>My answer:</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {yesNoOptions.map((option) => {
                      const optionValue = option === "Skip question" ? null : option;
                      return (
                        <button
                          key={option}
                          onClick={() => handleAnswerChange(question.id, optionValue)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            padding: '12px',
                            backgroundColor: yesNoAnswer && yesNoAnswer.myAnswer === optionValue ? '#e3f2fd' : 'white',
                            borderRadius: '4px',
                            border: yesNoAnswer && yesNoAnswer.myAnswer === optionValue ? '2px solid #2196F3' : '1px solid #ccc',
                            fontSize: '16px',
                            textAlign: 'left',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            if (!yesNoAnswer || yesNoAnswer.myAnswer !== optionValue) {
                              (e.target as HTMLButtonElement).style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!yesNoAnswer || yesNoAnswer.myAnswer !== optionValue) {
                              (e.target as HTMLButtonElement).style.backgroundColor = 'white';
                            }
                          }}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Partner preference */}
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={yesNoAnswer?.mirrored !== false}
                      onChange={(e) => {
                        handleMirrorToggle(question.id, e.target.checked);
                      }}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Mirror my answer for partner preference
                    </span>
                  </label>

                  {yesNoAnswer?.mirrored === false && yesNoAnswer && (
                    <div>
                      <div style={{ marginBottom: '15px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                          What do you want your partner to answer?
                        </h4>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            onClick={() => handlePartnerPreferenceChange(question.id, 'No')}
                            style={{
                              flex: 1,
                              padding: '12px',
                              fontSize: '16px',
                              backgroundColor: yesNoAnswer.partnerPreference === 'No' ? '#e3f2fd' : 'white',
                              border: yesNoAnswer.partnerPreference === 'No' ? '2px solid #2196F3' : '1px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: yesNoAnswer.partnerPreference === 'No' ? 'bold' : 'normal',
                            }}
                          >
                            No
                          </button>
                          <button
                            onClick={() => handlePartnerPreferenceChange(question.id, 'Yes')}
                            style={{
                              flex: 1,
                              padding: '12px',
                              fontSize: '16px',
                              backgroundColor: yesNoAnswer.partnerPreference === 'Yes' ? '#e3f2fd' : 'white',
                              border: yesNoAnswer.partnerPreference === 'Yes' ? '2px solid #2196F3' : '1px solid #ccc',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: yesNoAnswer.partnerPreference === 'Yes' ? 'bold' : 'normal',
                            }}
                          >
                            Yes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: yesNoAnswer?.mirrored === false ? '15px' : '0' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                      How much does this matter to you?
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {importanceOptions.map((option) => (
                        <button
                          key={String(option.value)}
                          onClick={() => handleImportanceChange(question.id, option.value)}
                          style={{
                            padding: '10px',
                            fontSize: '14px',
                            backgroundColor: yesNoAnswer?.importance === option.value ? '#e3f2fd' : 'white',
                            border: yesNoAnswer?.importance === option.value ? '2px solid #2196F3' : '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontWeight: yesNoAnswer?.importance === option.value ? 'bold' : 'normal',
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Note field */}
                  <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', marginTop: '15px' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                      Add a note (optional):
                    </h4>
                    <textarea
                      value={yesNoAnswer?.note ?? ''}
                      onChange={(e) => handleNoteChange(question.id, e.target.value)}
                      placeholder="Add context or details about your answer (max 1000 characters)"
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '10px',
                        fontSize: '14px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                      maxLength={1000}
                    />
                    <div style={{
                      marginTop: '5px',
                      fontSize: '12px',
                      color: (yesNoAnswer?.note?.length ?? 0) > 900 ? '#f57c00' : '#666',
                      textAlign: 'right',
                    }}>
                      {yesNoAnswer?.note?.length ?? 0} / 1000 characters
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        }

        // Render multiple choice question
        // For multiple choice questions
        const isSkipped = answer && answer.myAnswer === null;
        const multipleChoiceAnswered = answer && Array.isArray(answer.myAnswer) && answer.myAnswer.length > 0;
        const multipleChoicePartnerPrefEnabled = answer && Array.isArray(answer.partnerPreference);

        return (
          <div
            key={question.id}
            style={{
              marginBottom: '15px',
              padding: '15px',
              backgroundColor: multipleChoiceAnswered ? '#e8f5e9' : isSkipped ? '#e3f2fd' : '#f5f5f5',
              borderRadius: '8px',
              border: multipleChoiceAnswered ? '1px solid #4CAF50' : isSkipped ? '1px solid #2196F3' : '1px solid #ddd',
            }}
          >
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
              Class {question.class}
            </div>
            <div
              onClick={() => (multipleChoiceAnswered || isSkipped) && toggleQuestion(question.id)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: multipleChoiceAnswered || isSkipped ? 'pointer' : 'default',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '16px', flex: 1 }}>{question.text}</h3>
              {(multipleChoiceAnswered || isSkipped) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: isSkipped ? '#2196F3' : '#4CAF50', fontSize: '14px', fontWeight: 'bold' }}>
                      {isSkipped ? 'Skipped' : (() => {
                        // Sort answers by their position in the options array
                        const sortedAnswers = [...(answer.myAnswer as string[])].sort((a, b) => {
                          const indexA = question.options?.indexOf(a) ?? -1;
                          const indexB = question.options?.indexOf(b) ?? -1;
                          return indexA - indexB;
                        });
                        return `My answer: ${sortedAnswers.join(', ')}`;
                      })()}
                    </div>
                    {multipleChoicePartnerPrefEnabled && Array.isArray(answer.partnerPreference) && answer.partnerPreference.length > 0 && (
                      <div style={{ color: '#2196F3', fontSize: '12px', marginTop: '2px' }}>
                        Want partner: {(() => {
                          const sortedPrefs = [...answer.partnerPreference].sort((a, b) => {
                            const indexA = question.options?.indexOf(a) ?? -1;
                            const indexB = question.options?.indexOf(b) ?? -1;
                            return indexA - indexB;
                          });
                          return sortedPrefs.join(', ');
                        })()}
                        {answer.importance && (
                          <span style={{ marginLeft: '5px', fontStyle: 'italic' }}>
                            ({getImportanceLabel(answer.importance)})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: '20px', color: '#666' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                </div>
              )}
            </div>

            {isExpanded && (
              <div style={{ marginTop: '15px' }}>
                {/* My answer section */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>My answer:</h4>

                  {/* Skip question checkbox */}
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isSkipped}
                      onChange={() => handleMultipleChoiceChange(question.id, null, true)}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Skip question
                    </span>
                  </label>

                  {/* Options */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {question.options && question.options.map((option) => (
                      <label
                        key={option}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: Array.isArray(answer?.myAnswer) && answer.myAnswer.includes(option) ? '#e3f2fd' : 'white',
                          borderRadius: '4px',
                          border: Array.isArray(answer?.myAnswer) && answer.myAnswer.includes(option) ? '2px solid #2196F3' : '1px solid #ccc',
                          cursor: isSkipped ? 'not-allowed' : 'pointer',
                          opacity: isSkipped ? 0.5 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={Array.isArray(answer?.myAnswer) && answer.myAnswer.includes(option)}
                          onChange={() => !isSkipped && handleMultipleChoiceChange(question.id, option)}
                          disabled={isSkipped}
                          style={{ marginRight: '10px', cursor: isSkipped ? 'not-allowed' : 'pointer' }}
                        />
                        <span style={{ fontSize: '16px' }}>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Partner preference section */}
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={answer?.mirrored !== false}
                      onChange={(e) => handleMultipleChoicePartnerPrefToggle(question.id, e.target.checked)}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Mirror my answer for partner preference
                    </span>
                  </label>

                  {answer?.mirrored === false && answer && (
                    <div>
                      <div style={{ marginBottom: '15px' }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                          What do you want your partner to have selected?
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {question.options && question.options.map((option) => (
                            <label
                              key={option}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                backgroundColor: Array.isArray(answer?.partnerPreference) && answer.partnerPreference.includes(option) ? '#e3f2fd' : 'white',
                                borderRadius: '4px',
                                border: Array.isArray(answer?.partnerPreference) && answer.partnerPreference.includes(option) ? '2px solid #2196F3' : '1px solid #ccc',
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={Array.isArray(answer?.partnerPreference) && answer.partnerPreference.includes(option)}
                                onChange={() => handleMultipleChoicePartnerOptionChange(question.id, option)}
                                style={{ marginRight: '10px', cursor: 'pointer' }}
                              />
                              <span style={{ fontSize: '16px' }}>{option}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: answer?.mirrored === false ? '15px' : '0' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                      How much does this matter to you?
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {importanceOptions.map((option) => (
                        <button
                          key={String(option.value)}
                          onClick={() => handleImportanceChange(question.id, option.value)}
                          style={{
                            padding: '10px',
                            fontSize: '14px',
                            backgroundColor: answer?.importance === option.value ? '#e3f2fd' : 'white',
                            border: answer?.importance === option.value ? '2px solid #2196F3' : '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontWeight: answer?.importance === option.value ? 'bold' : 'normal',
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Note field */}
                  <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', marginTop: '15px' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666' }}>
                      Add a note (optional):
                    </h4>
                    <textarea
                      value={answer?.note ?? ''}
                      onChange={(e) => handleNoteChange(question.id, e.target.value)}
                      placeholder="Add context or details about your answer (max 1000 characters)"
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '10px',
                        fontSize: '14px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                      maxLength={1000}
                    />
                    <div style={{
                      marginTop: '5px',
                      fontSize: '12px',
                      color: (answer?.note?.length ?? 0) > 900 ? '#f57c00' : '#666',
                      textAlign: 'right',
                    }}>
                      {answer?.note?.length ?? 0} / 1000 characters
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: '30px' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Back to Profile
        </button>
      </div>
    </div>
  );
}

export default Questions;
